import React, { useEffect, useState, useCallback, useRef } from "react";
import './SearchView.css';
import Timeline from "../Timeline/Timeline";
import { Loader } from 'rsuite';
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "react-tooltip";
import { Accordion } from 'react-bootstrap';
import { DownloadIcon } from "../../assets/images/svgs/Icons";
import { formatFullDate } from "../../common/utils/helperFunctions";
import { searchObjectDetectionData, searchStacData } from "../../services/stac.service";
import { resetAllCollections } from "../../slices/orderSlice";
import * as mapActions from "../../slices/mapSlice";
import * as searchActions from "../../slices/searchSlice";
import * as turf from '@turf/turf';
import Filters from "../Filters/Filters";
import FiltersIcon from "../../assets/images/svgs/Filter.svg?react"
import MinimizeIcon from "../../assets/images/svgs/MinimizeIcon.svg?react"
import MaximizeIcon from "../../assets/images/svgs/MaximizeIcon.svg?react"
import Close from "../../assets/images/svgs/Close.svg?react"
import ArrowLeft from "../../assets/images/svgs/ArrowLeft.svg?react"
import ArrowRight from "../../assets/images/svgs/ArrowRight.svg?react"
import DropdownWhiteOpen from "../../assets/images/svgs/DropdownWhiteOpen.svg?react"

const SearchView = () => {

    const dispatch = useDispatch();

    const bbox = useSelector(state => state.map.bbox);
    const isFilterOption = useSelector(state => state.map.filterOption);
    const collectionsData = useSelector(state => state.collectionDetails.collections);
    const getCollectionName = (collectionId) =>
        collectionsData?.find((c) => c.collectionID === collectionId)?.name ?? collectionId;
    const INSTRUMENT_KEYS = ['instruments', 'instrument'];
    const getInstruments = (properties) => {
        const key = Object.keys(properties || {}).find((k) => INSTRUMENT_KEYS.includes(k.toLowerCase()));
        return key ? properties[key] : undefined;
    };

    const searchResults = useSelector(state => state.search.searchResults);
    const loadingSearchResults = useSelector(state => state.search.loadingSearchResults);
    const loadingObjectResults = useSelector(state => state.search.loadingObjectResults);
    const activeKey = useSelector(state => state.search.activeKey);
    const detectedKey = useSelector(state => state.search.detectedKey);
    const innerActiveKey = useSelector(state => state.search.innerActiveKey);
    const overlayLayer = useSelector(state => state.search.overlayLayer);
    const selectedOverlayKey = useSelector(state => state.search.selectedOverlayKey);
    const detectedObject = useSelector(state => state.search.detectedObject);
    const detectedObjectModal = useSelector(state => state.search.detectedObjectModal);


    const isLeftNavCollapsed = useSelector(state => state.leftNav.isCollapsed);

    const defaultSearchLimit = useSelector(state => state.filters.defaultSearchLimit);

    const activePanelRef = useRef(null);
    const detectedPanelRef = useRef(null);

    const [prevPageNumber, setPrevPageNumber] = useState(null);
    const [nextPageNumber, setNextPageNumber] = useState(null);
    const [pageRange, setPageRange] = useState('');
    const [isModalMinimized, setIsModalMinimized] = useState(false);

    const getPageRange = useCallback(() => {
        const lastItem = (nextPageNumber ? (nextPageNumber - 1) : (prevPageNumber + 1)) * defaultSearchLimit;
        const firstItem = lastItem - defaultSearchLimit + 1;
        const range = `${firstItem}-${lastItem}`;
        setPageRange(range);
    }, [nextPageNumber]);

    useEffect(() => {
        let prev = null;
        let next = null;
        if (Object.keys(searchResults).length > 0) {
            searchResults.links.forEach(link => {
                const pageNumber = link?.body?.page;
                if (link.rel === 'prev') {
                    prev = parseInt(pageNumber);
                }
                if (link.rel === 'next') {
                    next = parseInt(pageNumber);
                }
            });
            setPrevPageNumber(prev);
            setNextPageNumber(next);
            if (activeKey || activeKey == 0) {
                const feature = searchResults.features[activeKey];
                if (feature && Object.keys(feature).length > 0) {
                    dispatch(searchActions.setSelectedFeature(feature));
                    if (bbox && bbox.length > 0) dispatch(mapActions.setViewport(bbox));
                }
            }
        }
        getPageRange();
        return () => {
            dispatch(searchActions.setActiveKey("0"));
        };
    }, [searchResults, getPageRange, dispatch]);

    useEffect(() => {
        if (activePanelRef.current) {
            activePanelRef.current.scrollIntoView(
                {
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'start'
                }
            );
        }
    }, [activeKey]);

    useEffect(() => {
        if (detectedPanelRef.current) {
            detectedPanelRef.current.scrollIntoView(
                {
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'start'
                }
            );
        }
    }, [detectedKey]);

    const handleAccordionToggle = (eventKey) => {
        if (eventKey === null) {
            dispatch(searchActions.setActiveKey(null));
            dispatch(searchActions.resetSelectedFeature());
        } else {
            dispatch(searchActions.setActiveKey(eventKey));
            const feature = searchResults.features[eventKey];
            if (feature && Object.keys(feature).length > 0) {
                dispatch(searchActions.setSelectedFeature(feature));
                const center = feature.geometry.type === 'Polygon' ? turf.centroid(feature) :
                    feature;                    
                dispatch(mapActions.setPosition({position: center?.geometry?.coordinates, offset: { x: 0, y: 0 }}));
            }
        }
        dispatch(searchActions.setInnerActiveKey(null));
        handleDisable();
    };

    const handleDetectedAccordionToggle = (eventKey) => {
        if (eventKey === null) {
            dispatch(searchActions.setDetectedKey(null));
            dispatch(searchActions.resetSelectedDetectedObject());
        } else {
            dispatch(searchActions.setDetectedKey(eventKey));
            const feature = detectedObject.features[eventKey];
            if (feature && Object.keys(feature).length > 0) {
                dispatch(searchActions.setSelectedDetectedObject(feature));
                const center = feature.geometry.type === 'Polygon' ? turf.centroid(feature) :
                    feature;
                dispatch(mapActions.setPosition({position: center?.geometry?.coordinates , offset: { x: -200, y: 0 }}));
            }
        }
    };

    const handleInnerAccordionToggle = (eventKey) => {
        dispatch(searchActions.setInnerActiveKey(eventKey === innerActiveKey ? null : eventKey));
    };

    const handleActionButtonClick = (action) => {
        if (action === 'prev') {
            dispatch(searchStacData(prevPageNumber));
        } else if (action === 'next') {
            dispatch(searchStacData(nextPageNumber));
        }
    };

    const goToExportsPage = (event, eventKey) => {
        event.stopPropagation();
        dispatch(searchActions.setSelectedFeature(searchResults.features[eventKey]));
        dispatch(mapActions.resetSearchView());
        dispatch(mapActions.setExportsView());
    };

    const searchResultsAvailable = () => {
        return searchResults?.features?.length > 0;
    };

    const closeSearchView = () => {
        handleDisable();
        dispatch(resetAllCollections());
        dispatch(searchActions.setInnerActiveKey(null));
        dispatch(searchActions.resetSelectedFeature());
        dispatch(mapActions.resetCoordinatesGeometry());
        dispatch(mapActions.resetDisplayView());
        dispatch(mapActions.resetSearchView());
        dispatch(mapActions.resetSearchedAoi());
        dispatch(mapActions.hideSidebarRight());
        dispatch(searchActions.clearSimilarResults());
        dispatch(searchActions.clearSelectedFeatureIds());
    };

    const calculateArea = (geometry) => {
        const areaInMeters = turf.area(geometry);
        if (areaInMeters > 0) {
            const areaInKms = areaInMeters / 1000000;
            return areaInKms.toFixed(2);
        } else {
            return 0;
        }
    }

    const onChangeFilterOption = () => {
        isFilterOption === 'filter-option' ?
            dispatch(mapActions.resetFilterOption()) :
            dispatch(mapActions.setFilterOption('filter-option'));
    };

    const handleEnable = () => {
        dispatch(searchActions.enableOverlayLayer());
    }

    const handleDisable = () => {
        dispatch(searchActions.disableOverlayLayer());
        dispatch(searchActions.resetSelectedOverlayKey());
        closeDetectedObjectModal();
    }

    const openDetectedObjectModal = () => {
        dispatch(searchActions.setDetectedObjectModal());
    }
    const closeDetectedObjectModal = () => {
        dispatch(searchActions.resetDetectedObjectModal());
        dispatch(searchActions.setDetectedKey(null));
        dispatch(searchActions.resetSelectedDetectedObject());
    }

    const displayDetectedObjects = () => {
        return (
            <div className={`orderDescriptionBox detectedObjectDetails ${isLeftNavCollapsed ? 'collapsed' : ''}`}>
                <div className="search-results-header">
                    <div className="search-results-title">
                        Detected Results
                    </div>
                    <div className="search-header-actions">
                        <button className="icon-button" onClick={closeDetectedObjectModal}>
                            <Close />
                        </button>
                    </div>
                </div>
                <div className="detected-results-view">
                    <Accordion activeKey={detectedKey} onSelect={handleDetectedAccordionToggle}>
                        {
                            detectedObject.features.map((item, index) => (
                                <Accordion.Item ref={detectedKey == index ? detectedPanelRef : null} key={index} eventKey={String(index)}>
                                    <Accordion.Header>
                                        <div className="result-item-header-section">
                                            <div
                                                className="result-item-header"
                                                title={item.id}
                                            >
                                                {item.id}
                                            </div>
                                        </div>
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        <div className="result-info">
                                            <div className="result-key">Item ID</div>
                                            <div className="result-value" title={item.id}>{item.id}</div>
                                        </div>
                                        {item?.properties['datetime'] && (
                                            <div className="result-info">
                                                <div className="result-key">Data Acquisition</div>
                                                <div className="result-value">{formatFullDate(item?.properties['datetime'])}</div>
                                            </div>
                                        )}
                                        {(item?.properties['updated'] || item?.properties['processingDate']) && (
                                            <div className="result-info">
                                                <div className="result-key">Last Item Update</div>
                                                <div className="result-value">{formatFullDate(item?.properties['updated'] || item?.properties['processingDate'])}</div>
                                            </div>
                                        )}
                                        <div className="result-info">
                                            <div className="result-key">Sensor</div>
                                            <div className="result-value">{[].concat(getInstruments(item?.properties) ?? item?.properties?.sensor ?? '').join(', ')}</div>
                                        </div>
                                    </Accordion.Body>
                                </Accordion.Item>
                            ))
                        }
                    </Accordion >
                </div>
            </div>
        ) 
    }

    const displayResults = () => {
        return (
            <Accordion activeKey={activeKey} onSelect={handleAccordionToggle}>
                {
                    searchResults.features.map((item, index) => (
                        <Accordion.Item
                            ref={activeKey == index ? activePanelRef : null}
                            key={index}
                            eventKey={String(index)}
                            onMouseEnter={() => dispatch(searchActions.setHoveredResult(index))}
                            onMouseLeave={() => dispatch(searchActions.setHoveredResult(null))}
                        >
                            <Accordion.Header>
                                <div className="result-item-header-section">
                                    <div
                                        className="result-item-header"
                                        title={item.id}
                                    >
                                        {item.id}
                                    </div>
                                    <span
                                        data-tooltip-id="ExportButton"
                                        data-tooltip-content="Download"
                                        className="result-export-icon icon-button"
                                        onClick={(event) => goToExportsPage(event, index)}
                                    >
                                        <DownloadIcon />
                                    </span>
                                    <Tooltip id="ExportButton" />
                                </div>
                            </Accordion.Header>
                            <Accordion.Body>
                                <div className="result-info">
                                    <div className="result-key">Item ID</div>
                                    <div className="result-value" title={item.id}>{item.id}</div>
                                </div>
                                <div className="result-info">
                                    <div className="result-key">Collection</div>
                                    <div className="result-value">{getCollectionName(item.collection)}</div>
                                </div>
                                {getInstruments(item?.properties) && (
                                    <div className="result-info">
                                        <div className="result-key">Sensor</div>
                                        <div className="result-value">
                                            {[].concat(getInstruments(item.properties)).join(', ')}
                                        </div>
                                    </div>
                                )}
                                {item?.properties['datetime'] && (
                                    <div className="result-info">
                                        <div className="result-key">Data Acquisition</div>
                                        <div className="result-value">{formatFullDate(item?.properties['datetime'])}</div>
                                    </div>
                                )}
                                {/* {(item?.properties['updated'] || item?.properties['processingDate']) && (
                                    <div className="result-info">
                                        <div className="result-key">Last Item Update</div>
                                        <div className="result-value">{formatFullDate(item?.properties['updated'] || item?.properties['processingDate'])}</div>
                                    </div>
                                )} */}
                                <div className="result-info">
                                    <div className="result-key">Total Area</div>
                                    <div className="result-value">{calculateArea(item?.geometry)} sq kms</div>
                                </div>
                                <div className="result-advance-details">
                                    <Accordion activeKey={innerActiveKey} onSelect={handleInnerAccordionToggle}>
                                        <Accordion.Item eventKey="0">
                                            <Accordion.Header>
                                                Item Details
                                            </Accordion.Header>
                                            <Accordion.Body>
                                                {Object.keys(item?.properties).filter(key => key !== 'created' &&
                                                    key !== 'datetime' &&
                                                    key !== 'updated' &&
                                                    key !== 'geodata:collection_id' &&
                                                    !INSTRUMENT_KEYS.includes(key.toLowerCase()) &&
                                                    key !== 'geodata:item_id')
                                                    .map((key, index) => {
                                                        const value = item?.properties[key];
                                                        return (
                                                            <div className="result-info" key={index}>
                                                                <div className="result-key">{key}</div>
                                                                <div className="result-value">
                                                                    {Array.isArray(value) ? (
                                                                        value.join(', ')
                                                                    ) : typeof value === 'object' && value !== null ? (
                                                                        Object.entries(value).map(([subKey, subValue]) => `${subKey}: ${subValue}`).join(', ')
                                                                    ) : (
                                                                        value
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )
                                                    })}
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    </Accordion>
                                </div>
                                <div className="result-overlays">
                                    <div className="result-overlays-header">
                                        <div className="result-item-header">Overlay Layers</div>
                                        <div className="overlay-tabs">
                                            <div className={`overlay-btn ${overlayLayer ? 'active-btn' : ''}`} onClick={handleEnable}>Enabled</div>
                                            <div className={`overlay-btn ${overlayLayer ? '' : 'active-btn'}`} onClick={handleDisable}>Disabled</div>
                                        </div>
                                    </div>
                                    {overlayLayer && (
                                        <div className="overlay-container">
                                            {Object.entries(item?.properties)
                                            .filter(([key]) => key.startsWith("geodata:derivative_collection"))
                                            .map(([key, value]) => {
                                                const label = key.split(":").pop().split("(")[0];
                                                return (
                                                <div key={value} className={`overlay-item ${selectedOverlayKey === value ? 'active-checkbox' : ''}`}>
                                                    <div className="overlay-header-lable">
                                                        <input
                                                        type="checkbox"
                                                        className="overlay-checkbox"
                                                        checked={selectedOverlayKey === value}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                dispatch(searchActions.setSelectedOverlayKey(value));
                                                                dispatch(searchObjectDetectionData(value));
                                                                closeDetectedObjectModal();
                                                            } else {
                                                                closeDetectedObjectModal();
                                                            }
                                                        }}
                                                        />
                                                        <span className="overlay-label">{label}</span>
                                                    </div>
                                                    {selectedOverlayKey === value && (
                                                        <>
                                                            {loadingObjectResults ? (
                                                                <Loader inverse />
                                                            ) : (
                                                            detectedObject?.numberMatched > 0 && (
                                                                <>
                                                                <div className="object-detected-count" onClick={openDetectedObjectModal}>
                                                                    <span>{detectedObject.numberMatched}</span>
                                                                    <DropdownWhiteOpen style={{ rotate: "-90deg" }} />
                                                                </div>

                                                                {detectedObjectModal && displayDetectedObjects()}
                                                                </>
                                                            )
                                                            )}
                                                        </>
                                                        )}
                                                </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </Accordion.Body>
                        </Accordion.Item>
                    ))
                }
            </Accordion >
        )
    }

    return (
        <>
            {isFilterOption === 'filter-option' && <Filters />}
            <div className={`orderDescriptionBox searchDetails ${isLeftNavCollapsed ? 'collapsed' : ''}`}>
                <div className="search-results-header bottomBorder">
                    <div className="search-results-title">
                        Search Results {!loadingSearchResults && `(${searchResults.numberMatched})`}
                    </div>
                    <div className="search-header-actions">
                        <button
                            data-tooltip-id="SearchViewFilterBtn"
                            data-tooltip-content="Filter"
                            data-tooltip-place="top"
                            className="icon-button"
                            onClick={onChangeFilterOption}
                        >
                            <FiltersIcon fill={isFilterOption ? "#CCF54E" : "#F0F0F0"} />
                        </button>
                        <Tooltip id="SearchViewFilterBtn" />
                        <button className="icon-button" onClick={() => setIsModalMinimized(!isModalMinimized)}>
                            {!isModalMinimized && <MinimizeIcon />}
                            {isModalMinimized && <MaximizeIcon />}
                        </button>
                        <button className="icon-button" onClick={closeSearchView}>
                            <Close />
                        </button>
                    </div>
                </div>
                {!isModalMinimized && <>

                    <div className="results-view">
                        {
                            loadingSearchResults ?
                                <div>Searching...</div> :
                                searchResultsAvailable() ?
                                    displayResults()
                                    : <div>No Results Found</div>
                        }
                    </div>
                    {searchResultsAvailable() &&
                        <div className="search-actions topBorder">
                            <button
                                className={`icon-button ${!prevPageNumber ? 'disabled' : ''}`}
                                disabled={!prevPageNumber}
                                onClick={() => handleActionButtonClick('prev')}
                            >
                                <ArrowLeft />
                            </button>
                            <div> {pageRange} </div>
                            <button
                                className={`icon-button ${!nextPageNumber ? 'disabled' : ''}`}
                                disabled={!nextPageNumber}
                                onClick={() => handleActionButtonClick('next')}
                            >
                                <ArrowRight />
                            </button>
                        </div>
                    }
                </>
                }
            </div>
            <Timeline />
        </>
    )
};

export default SearchView;